import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { EmptyPage } from '@/components/EmptyPage';
import { useFindOpsOrderQuery } from '@/graphql/queries/findOpsOrder.generated';
import { useBreadcrumbs } from '@/components/Breadcrumbs/useBreadcrumbs';
import i18n from '@/utils/i18n';

import type { OpsOrderDetail } from './types';

interface OrderDetailPageProps {
  orderId: string;
  breadcrumbLabel: (order: OpsOrderDetail) => string;
  backRoute: string;
  backLabel: string;
  children: (props: {
    order: OpsOrderDetail;
    refetch: () => Promise<unknown>;
  }) => React.ReactNode;
  additionalLoading?: boolean;
}

export function OrderDetailPage({
  orderId,
  breadcrumbLabel,
  backRoute,
  backLabel,
  children,
  additionalLoading,
}: OrderDetailPageProps) {
  const history = useHistory();
  const { data, loading, error, refetch } = useFindOpsOrderQuery({
    variables: { id: orderId },
    fetchPolicy: 'network-only',
  });

  const order = data?.findOpsOrder;

  useBreadcrumbs({
    id: 'order-details',
    items: [
      {
        label: !order ? i18n.t('Common:loading') : breadcrumbLabel(order),
      },
    ],
  });

  const handleBack = () => history.push(backRoute);

  if (loading || additionalLoading) {
    return (
      <div className="tw:flex tw:items-center tw:justify-center tw:p-20">
        <Spinner />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="tw:mx-auto tw:flex tw:max-w-[1340px] tw:flex-col tw:items-center tw:gap-4 tw:p-6 tw:pt-20">
        <EmptyPage
          title={i18n.t(error ? 'Common:error' : 'Order:orderNotFound')}
          iconName="person-walk-directions"
        >
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeftIcon className="tw:mr-2 tw:size-4" />
            {backLabel}
          </Button>
        </EmptyPage>
      </div>
    );
  }

  return <>{children({ order, refetch })}</>;
}
